import { getVisibleExtraSections } from "./detailSections";
import { compareNatural } from "./text";
import type { Intervention } from "./types";

export type ListSortKey =
  "number" | "name" | "location" | "color" | "links" | "description" | `h:${string}`;
export type SortDirection = "asc" | "desc";

/**
 * Detail level of the list: 0 = base columns, 1 = plus the `Data_h#` columns,
 * 2 = plus the description column.
 */
export type ListDetailsLevel = 0 | 1 | 2;

export interface DynamicColumn {
  key: `h:${string}`;
  title: string;
  label: string;
}

/** `Data_h#<Title>` sections of an item, joined per title. */
export function getHighlightedSections(
  item: Intervention,
  language: string,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const section of getVisibleExtraSections(item.extraSections, language)) {
    if (section.style !== "h" || !section.title) continue;
    map[section.title] = map[section.title]
      ? `${map[section.title]} | ${section.text}`
      : section.text;
  }
  return map;
}

function truncate(text: string, maxLength: number): string {
  const value = text.trim();
  return value.length <= maxLength ? value : `${value.slice(0, maxLength)}...`;
}

/** One sortable column per distinct `Data_h#` title across all items. */
export function buildDynamicColumns(items: Intervention[], language: string): DynamicColumn[] {
  const titles = new Set<string>();
  for (const item of items)
    Object.keys(getHighlightedSections(item, language)).forEach((title) => titles.add(title));
  return [...titles]
    .sort(compareNatural)
    .map((title) => ({ key: `h:${title}`, title, label: truncate(title, 25) }));
}

export function localized(text: { no: string; en: string }, language: string): string {
  return (language === "en" ? text.en : text.no) || text.no;
}

export interface ListSortContext {
  language: string;
  /** Classification label used by the "color" column. */
  colorLabel: (item: Intervention) => string;
}

/** Comparator for the list; ties are broken by item number. */
export function compareListItems(
  key: ListSortKey,
  direction: SortDirection,
  context: ListSortContext,
) {
  const { language, colorLabel } = context;
  const primary = (a: Intervention, b: Intervention): number => {
    switch (key) {
      case "number": {
        const [na, nb] = [Number(a.itemNumber), Number(b.itemNumber)];
        return Number.isFinite(na) && Number.isFinite(nb)
          ? na - nb
          : compareNatural(a.itemNumber, b.itemNumber);
      }
      case "name":
        return compareNatural(localized(a.display, language), localized(b.display, language));
      case "location":
        return compareNatural(a.region, b.region);
      case "color":
        return compareNatural(colorLabel(a), colorLabel(b));
      case "links":
        return a.links.length - b.links.length;
      case "description":
        return compareNatural(
          localized(a.description, language),
          localized(b.description, language),
        );
      default: {
        const title = key.slice(2);
        return compareNatural(
          getHighlightedSections(a, language)[title] ?? "",
          getHighlightedSections(b, language)[title] ?? "",
        );
      }
    }
  };
  return (a: Intervention, b: Intervention): number => {
    const result = primary(a, b) || compareNatural(a.itemNumber, b.itemNumber);
    return direction === "asc" ? result : -result;
  };
}
